// src/App.js
//
// KEY FIX: <Suspense> is removed from around the whole <Routes> tree.
// It is now placed INSIDE Layout.jsx, wrapping only the <Outlet />.
// This means Sidebar, Header, and Footer are never unmounted during navigation.
//
// Eager imports (Layout, UnauthorizedPage) remain so the shell is instantly
// available. All page-level components remain lazy so their JS bundles are
// only downloaded when the user first visits that route.

import React, { lazy, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import queryClient from "./utils/queryClient";
import { NotificationProvider } from "./contexts/NotificationContext";
import { LoadingProvider, useLoading } from "./contexts/LoadingContext";
import { getCurrentStep, setLoadingHandler } from "./utils/axiosInstance";
import GlobalLoader from "./components/GlobalLoader";
import TawkTo from "./components/Tawk";
import { isAppProductManagementAllowed } from "./utils/appProductAccess";

// ── Eager (tiny, needed immediately) ──
import Layout from "./components/Layout";
import UnauthorizedPage from "./pages/UnauthorizedPage.jsx";

// ── Lazy (loaded only when user visits that route) ──
const LoginPage               = lazy(() => import("./pages/common/login/LoginPage"));
const TokenLoginHandler       = lazy(() => import("./pages/TokenLoginHandler"));
const OnboardingStep1         = lazy(() => import("./pages/ebay/onboarding/OnboardingStep1"));
const MarketplaceSelection     = lazy(() => import("./pages/common/marketplaceSelector/MarketplaceSelection"));
const OnboardingStep2         = lazy(() => import("./pages/ebay/onboarding/OnboardingStep2"));
const OnboardingStep3         = lazy(() => import("./pages/ebay/onboarding/OnboardingStep3"));
const Dashboard               = lazy(() => import("./pages/common/dashboard/Dashboard"));
const Products                = lazy(() => import("./pages/common/products/Products"));
const AppProducts             = lazy(() => import("./pages/common/products/AppProducts"));
const AppProductDetail        = lazy(() => import("./pages/common/products/AppProductDetail"));
const ProductDetailPage       = lazy(() => import("./pages/common/productDetails/ProductDetailPage.tsx"));
const BusinessPolicy          = lazy(() => import("./pages/ebay/businessPolicy/BusinessPolicy"));
const TemplateGrid            = lazy(() => import("./pages/ebay/templateGrid/TemplateGrid"));
const ProfileGrid             = lazy(() => import("./pages/common/profileGrid/ProfileGrid"));
const EbayAccountsPage        = lazy(() => import("./pages/ebay/ebayAccounts/EbayAccountsPage"));
const EbayProducts            = lazy(() => import("./pages/ebay/ebayProducts/EbayProducts"));
const SettingsPage            = lazy(() => import("./pages/ebay/settings/SettingsPage"));
const ActivityPage            = lazy(() => import("./pages/common/activity/ActivityPage"));
const Orders                  = lazy(() => import("./pages/common/orders/Orders"));
const Importer                = lazy(() => import("./pages/common/importer/Importer"));
const ImporterProductDetail   = lazy(() => import("./pages/common/importer/ImporterProductDetail"));
const ViewOrderPage           = lazy(() => import("./pages/common/orders/ViewOrderPage.tsx"));
const TestOrderCreator        = lazy(() => import("./pages/TestOrderCreator.tsx"));
const ViewInventoryTemplate   = lazy(() => import("./pages/ViewInventoryTemplate"));
const PlansPage               = lazy(() => import("./pages/common/plans/PlansPage"));
const UserProfile             = lazy(() => import("./pages/common/userProfile/UserProfile"));
const CategoryTemplatePage    = lazy(() => import("./pages/ebay/categoryTemplate/CategoryTemplatePage.jsx"));
const ProfilePage             = lazy(() => import("./pages/ebay/profile/ProfilePage"));
const ContactUsPage           = lazy(() => import("./pages/common/contact/ContactUsPage.jsx"));
const PolicyView              = lazy(() => import("./pages/ebay/policy/PolicyView.jsx"));
const FAQSection              = lazy(() => import("./pages/common/faq/FAQSection.js"));
const PolicyPage              = lazy(() => import("./pages/PolicyPage.jsx"));
const ViewProfile             = lazy(() => import("./pages/ebay/profile/ViewProfile"));
const CreateInventoryTemplate = lazy(() => import("./pages/CreateTemplates/CreateInventoryTemplate"));
const CreatePriceTemplate     = lazy(() => import("./pages/CreateTemplates/CreatePriceTemplate"));
const CreateTitleTemplate     = lazy(() => import("./pages/CreateTemplates/CreateTitleTemplate"));

function AppProductManagementRoute({ children }) {
  return isAppProductManagementAllowed() ? children : <Navigate to="/products" replace />;
}

// ── Connects axios loading handler ──
// IMPORTANT: axiosInstance should only call setLoading for mutations
// (POST, PUT, DELETE, PATCH). GET requests must NOT trigger the global loader.
// See the axiosInstance note below.
function StepBootstrap({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [backendStep, setBackendStep] = useState(null);

  const hasToken = Boolean(sessionStorage.getItem("accessToken"));
  const isPublicRoute = [
    "/token-login",
    "/login",
    "/unauthorized",
    "/terms",
    "/privacy",
  ].includes(location.pathname);

  useEffect(() => {
    // TokenLoginHandler owns the token exchange. Once it redirects into the
    // app, always resolve the route from the backend instead of stale browser
    // session state. This also covers direct Wix/admin launches.
    if (!hasToken || isPublicRoute) {
      setBackendStep(null);
      return undefined;
    }

    let active = true;
    getCurrentStep()
      .then((response) => {
        if (!active) return;

        const step = Number(response.data);
        if (!Number.isFinite(step) || step < 0 || step > 4) {
          throw new Error(`Invalid getStep response: ${response.data}`);
        }
        const resolvedStep = step === 0 ? 1 : step;
        sessionStorage.setItem("currentStep", String(resolvedStep));
        setBackendStep(resolvedStep);

        if (resolvedStep < 4) {
          const expectedPath = `/onboarding/step${resolvedStep}`;
          if (location.pathname === "/" || location.pathname !== expectedPath) {
            navigate(expectedPath, { replace: true });
          }
        } else if (location.pathname === "/") {
          navigate("/dashboard", { replace: true });
        }
      })
      .catch((error) => {
        if (!active) return;
        console.error(
          "Failed to resolve onboarding step from https://api.ecstacommerce.com/api/wix/oauth/getStep:",
          error
        );
        // Never use stale sessionStorage here. Rendering/redirecting to step
        // 1 after a failed request hides the real auth or API problem.
        sessionStorage.removeItem("currentStep");
        navigate("/unauthorized", { replace: true });
      });

    return () => {
      active = false;
    };
  }, [hasToken, isPublicRoute, navigate]);

  if (hasToken && !isPublicRoute && backendStep === null) {
    return <div aria-label="Loading application" />;
  }

  return children(backendStep);
}

function AppRoutes() {
  const loadingHandler = useLoading();
  setLoadingHandler(loadingHandler);

  return (
    <>
      {/* GlobalLoader is now a slim top progress bar — never fullscreen */}
      <GlobalLoader />
      <TawkTo />

      {/*
        NO <Suspense> here.
        The Suspense boundary lives inside Layout.jsx, wrapping only <Outlet />.
        That way the shell (Sidebar + Header + Footer) is never suspended.
      */}
      <Router basename="/ebay">
        <StepBootstrap>
          {(currentStep) => (
          <Routes>

          {/* ── Public routes ── */}
          {/*
            These routes are outside Layout so they don't get the sidebar.
            They each have their own Suspense boundary inside Layout or are
            small enough to bundle eagerly. Wrap them individually if needed.
          */}
          <Route path=":type"        element={<PolicyPage />} />
          <Route path="/token-login" element={<TokenLoginHandler />} />
          <Route path="/login"       element={<LoginPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* ── Onboarding (no sidebar/header) ── */}
          <Route path="/onboarding/step0" element={<MarketplaceSelection />} />
          <Route path="/onboarding/step1" element={<OnboardingStep1 />} />
          <Route path="/onboarding/step2" element={<OnboardingStep2 />} />
          <Route path="/onboarding/step3" element={<OnboardingStep3 />} />

          {/*
            ── Main app ──
            Layout renders Sidebar + Header + <Outlet /> + Footer.
            The <Suspense> boundary is INSIDE Layout, around the Outlet.
            Navigating between child routes only suspends the content area.
          */}
          <Route
            path="/"
            element={
              currentStep === null || currentStep >= 4
                ? <Layout />
                : <Navigate to={`/onboarding/step${currentStep}`} replace />
            }
          >
            <Route path="dashboard"        element={<Dashboard />} />
            <Route path="products"         element={<Products />} />
            <Route path="app-products" element={<AppProductManagementRoute><AppProducts /></AppProductManagementRoute>} />
            <Route path="app-products/:appProductId" element={<AppProductManagementRoute><AppProductDetail /></AppProductManagementRoute>} />
            <Route path="products/:wixProductId" element={<ProductDetailPage />} />
            <Route path="BusinessPolicy"   element={<BusinessPolicy />} />
            <Route path="templates"        element={<TemplateGrid />} />
            <Route path="profiles"         element={<ProfileGrid />} />
            <Route path="profile-page"     element={<ProfilePage />} />
            <Route path="profile-page/:profileId" element={<ProfilePage />} />
            <Route path="ebay-accounts"    element={<EbayAccountsPage />} />
            <Route path="ebay-products"    element={<EbayProducts />} />
            <Route path="create-inventory-template" element={<CreateInventoryTemplate />} />
            <Route path="create-price-template"     element={<CreatePriceTemplate />} />
            <Route path="view-price-template/:templateId" element={<CreatePriceTemplate />} />
            <Route path="create-title-template"     element={<CreateTitleTemplate />} />
            <Route path="view-inventory-template/:templateId" element={<ViewInventoryTemplate />} />
            <Route path="settings"         element={<SettingsPage />} />
            <Route path="Faq"              element={<FAQSection />} />
            <Route path="activity"         element={<ActivityPage />} />
            <Route path="contact-us"       element={<ContactUsPage />} />
            <Route path="orders"           element={<Orders />} />
            <Route path="importer/:productId" element={<ImporterProductDetail />} />
            <Route path="importer"         element={<Importer />} />
            <Route path="orders/:ebayOrderId" element={<ViewOrderPage />} />
            <Route path="create-order"     element={<TestOrderCreator />} />
            <Route path="plans"            element={<PlansPage />} />
            <Route path="user-profile"     element={<UserProfile />} />
            <Route path="template"         element={<CategoryTemplatePage />} />
            <Route path="template/:templateId" element={<CategoryTemplatePage />} />
            <Route path="policy"           element={<PolicyView />} />
            <Route path="policy/:policyId" element={<PolicyView />} />
            <Route path="view-profile/:profileId" element={<ViewProfile />} />
          </Route>

          </Routes>
          )}
        </StepBootstrap>
      </Router>
    </>
  );
}

// ── Root ──
export default function App() {
  return (
    <NotificationProvider>
      <LoadingProvider>
        <QueryClientProvider client={queryClient}>
          <AppRoutes />
        </QueryClientProvider>
      </LoadingProvider>
    </NotificationProvider>
  );
}
